import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { createOrganization } from "@/api/organizations";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { queryKeys } from "@/lib/query-keys";
import { useOrganizationStore } from "@/store/organization";
import {
  BrandingSidebar,
  OnboardingLayout,
} from "./components/onboarding-layout";

/**
 * Schema for organization creation form
 */
const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Organization name must be at least 2 characters." })
    .max(100, {
      message: "Organization name must be at most 100 characters.",
    }),
});

type FormValues = z.infer<typeof formSchema>;

/**
 * Creates an organization, selects it for the current session, and invalidates
 * the organization list cache. The form remains mounted until the external
 * organization switch settles, and a failed switch retries selection without
 * creating a duplicate organization.
 *
 * @returns The organization-creation onboarding view.
 */
export const CreateOrganizationView = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createdOrganization, setCreatedOrganization] = useState<Awaited<
    ReturnType<typeof createOrganization>
  > | null>(null);
  const [isCompletingSelection, setIsCompletingSelection] = useState(false);
  const setOrganization = useOrganizationStore((s) => s.setOrganization);
  const isSwitching = useOrganizationStore((s) => s.isSwitching);
  const queryClient = useQueryClient();

  const completeOrganizationSelection = async (
    organization: Awaited<ReturnType<typeof createOrganization>>,
  ) => {
    setIsCompletingSelection(true);
    try {
      await setOrganization(organization);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all(),
      });
      setCreatedOrganization(null);
    } catch {
      setCreatedOrganization(organization);
      setErrorMessage(
        "Organization created, but we could not switch to it. Please try again.",
      );
    } finally {
      setIsCompletingSelection(false);
    }
  };

  const createOrganizationMutation = useMutation({
    mutationFn: async (name: string) => {
      return createOrganization(name);
    },
    onSuccess: async (organization) => {
      await completeOrganizationSelection(organization);
    },
    onError: (_err) => {
      setErrorMessage("Failed to create organization. Please try again.");
    },
  });

  const isLoading =
    createOrganizationMutation.isPending ||
    isSwitching ||
    isCompletingSelection;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "" },
  });

  const onSubmit = (values: FormValues) => {
    setErrorMessage(null);
    if (createdOrganization) {
      void completeOrganizationSelection(createdOrganization);
      return;
    }
    createOrganizationMutation.mutate(values.name);
  };

  return (
    <OnboardingLayout
      sidebar={
        <BrandingSidebar
          description="Create your organization to manage API keys, webhooks, billing and usage for your health AI integration."
          featuresHeading="Your console provides:"
        />
      }
    >
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          Create Your Organization
        </h2>
        <p className="mt-3 text-muted-foreground">
          Set up your organization to get started with our platform.
        </p>
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Organization Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Acme Inc."
                    {...field}
                    autoFocus
                    disabled={isLoading || !!createdOrganization}
                    className="h-12 text-base"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-12 text-base group"
            disabled={isLoading}
            size="lg"
          >
            {isLoading ? (
              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
            ) : createdOrganization ? (
              "Retry Organization Switch"
            ) : (
              "Create Organization"
            )}
            <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </form>
      </Form>
    </OnboardingLayout>
  );
};
